import {
  ChangeDetectorRef,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import {
  Appointment,
  Dossier,
  Fitter,
  getGlassNameGender,
  getGlassNameKey,
  getOperationType,
  getOperationWithGenderedGlassKey,
  RequestAppointment,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  getTranslatableDuration,
  resolveInterventionDuration,
} from '@sc/utils';

import { AsyncPipe, DecimalPipe, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { mapTo, switchMap, tap } from 'rxjs/operators';
import { MomentParseZonePipe } from '../../pipes/parse-zone.pipe';
import { IconComponent } from '../icon/icon.component';
import { RatingComponent } from '../rating/rating.component';

@Component({
  selector: 'sc-appointment-infos-box',
  styleUrls: ['./appointment-infos-box.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    IconComponent,
    RatingComponent,
    AsyncPipe,
    DecimalPipe,
    TranslateModule,
    LocaleDatePipe,
    MomentParseZonePipe,
  ],
  template: `
    <div class="information">
      <p
        id="apmt-infos"
        class="sc-design-title-h2"
        *ngIf="mainTitle?.length"
        role="heading"
        [attr.aria-level]="mainTitleLevel"
      >
        {{ mainTitle }}
      </p>
      <div class="data appointment">
        <div
          class="sc-design-text-3-bold"
          *ngIf="_currentDossier?.appointment?.time"
          role="heading"
          [attr.aria-level]="subTitleLevel"
        >
          {{
            'GlobalApplicativeConstants.time.dateFormat'
              | translate
                : {
                    dateDay:
                      _currentDossier?.appointment?.time
                      | parseZoneTimeString
                        : timezoneService.getTimezone(
                            _currentDossier?.appointment
                          )
                      | localeDate: 'fullDate'
                      | async,
                    isHalfDay: dossierSB.shouldDisplayAppointmentHalfDay(
                      _currentDossier?.appointment
                    ),
                    dateTime:
                      _currentDossier?.appointment?.time
                      | parseZoneTimeString
                        : timezoneService.getTimezone(
                            _currentDossier?.appointment
                          )
                      | localeDate: 'shortTime'
                      | async,
                    appointmentHalfDay: dossierSB.getTime(
                      _currentDossier?.appointment
                    )
                  }
          }}
        </div>
        <div
          class="vehicle-info operation-details"
          *ngIf="displaySinisterGlass()"
        >
          <div class="row">
            <sc-icon name="accident"></sc-icon>
            <p class="sc-design-text-3">
              {{ getSinisterGlass() | translate }}
            </p>
          </div>
        </div>
        <div class="vehicle-info">
          <div
            class="row"
            *ngIf="
              featureFlippingService.isBrandFeatureActivated(
                currentFitter?.brandId,
                'can-declare-replacement-vehicle-fitter'
              ) &&
              featureFlippingService.isFeatureActivated(
                'can-declare-replacement-vehicle-insurer'
              )
            "
          >
            <sc-icon name="car"></sc-icon>
            <p class="sc-design-text-3">
              {{
                _currentDossier?.appointment?.isReplacementVehicleSelected ??
                _currentDossier?.requestAppointment
                  ?.isReplacementVehicleSelected
                  ? ('LastStep.vehicle' | translate)
                  : ('LastStep.withoutvehicle' | translate)
              }}
            </p>
          </div>
          <div class="row immobilisation-time">
            <sc-icon name="time"></sc-icon>
            <p class="sc-design-text-3">
              {{
                (showAdasDurationRange()
                  ? 'LastStep.immobilizationTimeAdas'
                  : 'LastStep.immobilizationTime'
                )
                  | translate
                    : {
                        duration: getDuration(operation),
                        durationAdas: getDurationAdas()
                      }
              }}
            </p>
          </div>
        </div>
        <div
          class="sc-design-text-3-bold"
          role="heading"
          [attr.aria-level]="subTitleLevel"
          tabindex="-1"
        >
          {{
            'LastStep.SummaryPage.interventionLocationSubTitle'
              | translate
                : {
                    atHome: +(
                      currentAppointment?.outsideIntervention ||
                      currentDossier?.requestAppointment?.outsideIntervention
                    )
                  }
          }}
        </div>
        <div
          class="fitter-info"
          *ngIf="
            fittersSB.isNotItinerant(currentFitter) && displayItiniraryInfos
          "
        >
          <div
            class="info-element sc-design-text-3"
            *ngIf="!hideTimeAndDistance"
          >
            <sc-icon name="road"></sc-icon>
            <span>
              {{
                'GlobalApplicativeConstants.distance.interventionDistanceKm'
                  | translate
                    : {
                        distance:
                          itineraryDistanceInMeters >= 0
                            ? (itineraryDistanceInMeters / 1000
                              | number: '1.0-1')
                            : '-'
                      }
              }}
            </span>
          </div>

          <div
            class="info-element sc-design-text-3"
            *ngIf="!hideTimeAndDistance"
          >
            <sc-icon name="time"></sc-icon>
            <span>
              {{
                'GlobalApplicativeConstants.time.interventionTimeInMinutes'
                  | translate
                    : {
                        minStr:
                          itineraryDurationInSeconds >= 0
                            ? (itineraryDurationInSeconds / 60
                              | number: '1.0-0')
                            : '-'
                      }
              }}
            </span>
          </div>
          <sc-rating
            [rating]="currentFitter?.google?.rating"
            [brandId]="currentFitter?.brandId"
          ></sc-rating>
        </div>
        <div class="address">
          <div class="row">
            <sc-icon name="map_location"></sc-icon>
            <p class="sc-design-text-3">
              {{
                getAdressWithoutLastComma(
                  currentAppointment?.interventionLocation?.address
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AppointmentInfosBoxComponent {
  _currentDossier: Dossier;
  @Input() set currentDossier(newDossier: Dossier) {
    this.onLoadDossier(newDossier);
  }
  @Input() hideTimeAndDistance = false;
  @Input() mainTitle: string;
  @Input() displayItiniraryInfos = false;
  @Input() mainTitleLevel = 3;
  @Input() subTitleLevel = 4;
  currentAppointment: Appointment | RequestAppointment;
  currentFitter: Fitter;
  itineraryDurationInSeconds: number;
  itineraryDistanceInMeters: number;
  operation: string;

  constructor(
    public fittersSB: FittersSandboxService,
    private readonly sandboxSB: SandboxService,
    public dossierSB: DossierSandboxService,
    public timezoneService: TimezoneService,
    public translator: TranslatorService,
    public configurationService: ConfigurationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public featureFlippingService: FeatureFlippingManagerService
  ) {}

  onLoadDossier(newDossier: Dossier) {
    if (!newDossier) {
      return;
    }

    this._currentDossier = newDossier;

    this.currentAppointment =
      this._currentDossier.appointment ??
      this._currentDossier?.requestAppointment;
    this.operation = getOperationType(
      this._currentDossier.claim.glass
    ).toLowerCase();

    this.fittersSB
      .fetchFitter({
        id:
          this._currentDossier.appointment?.fitterId ??
          this._currentDossier?.requestAppointment?.fitterId,
        brandId:
          this._currentDossier.appointment?.brandId ??
          this._currentDossier?.requestAppointment?.brandId,
      })
      .pipe(
        switchMap((fitter) =>
          this.sandboxSB.getDossierItinerary(this._currentDossier, fitter).pipe(
            tap((itinerary) => {
              if (this.fittersSB.isNotItinerant(fitter) && !!itinerary) {
                this.itineraryDurationInSeconds = itinerary.duration;
                this.itineraryDistanceInMeters = itinerary.distance;
                this.changeDetectorRef.detectChanges();
              }
            }),
            mapTo(fitter)
          )
        )
      )
      .subscribe(
        (fitter) => {
          if (fitter) {
            this.currentFitter = fitter;
            this.changeDetectorRef.detectChanges();
          }
        },
        () => {
          this.currentFitter = null;
        }
      );
  }

  displaySinisterGlass(): boolean {
    return !!this._currentDossier?.claim?.glass?.glass;
  }

  getSinisterGlass(): string {
    return this.translator.getInstantTranslation(
      getOperationWithGenderedGlassKey(
        this._currentDossier?.claim?.operationType
      ),
      {
        glassName: this.translator
          .getInstantTranslation(
            getGlassNameKey(this._currentDossier.claim.glass)
          )
          .toLowerCase(),
        gender: getGlassNameGender(this._currentDossier?.claim?.glass),
      }
    );
  }

  getDuration(operation) {
    const usedDuration = resolveInterventionDuration(
      this.configurationService.getConfig(),
      operation,
      this.currentAppointment?.immobilisationDuration
    );

    const durationObj = getTranslatableDuration(usedDuration);
    return this.translator.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  getDurationAdas(): string {
    const usedDuration = resolveInterventionDuration(
      this.configurationService.getConfig(),
      'replaceAdas'
    );
    const durationObj = getTranslatableDuration(usedDuration);
    return this.translator.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  showAdasDurationRange(): boolean {
    return (
      this.featureFlippingService.isFeatureActivated(
        'display-adas-duration-range'
      ) &&
      this._currentDossier?.claim?.glass?.glass === 'windshield' &&
      this.operation === 'replace'
    );
  }

  getAdressWithoutLastComma(location: string) {
    const lastIndex = location?.lastIndexOf(',');

    if (lastIndex !== -1 && !!location) {
      return location?.slice(0, lastIndex) + location?.slice(lastIndex + 1);
    }

    return location;
  }
}
